import { useEffect, useRef } from 'react';
import type { Dispatch, SetStateAction, RefObject } from 'react';
import type { CampaignSource } from 'features/campaigns';

interface ScrollTrackingRefs {
  isProgrammaticScroll: RefObject<boolean>;
  targetCampaignId: RefObject<string | null>;
  isManualScrollingRef: RefObject<boolean>;
  visualActiveIdRef: RefObject<string | null>;
  selectionTimerRef: RefObject<ReturnType<typeof setTimeout> | undefined>;
}

interface ScrollTrackingProps {
  carouselRef: RefObject<HTMLDivElement | null>;
  campaigns: CampaignSource[];
  isArrowsHovered: boolean;
  refs: ScrollTrackingRefs;
  getCachedElement: (id: string) => HTMLElement | null;
  setSelectedCampaignId: (id: string) => void;
  setVisualActiveId: Dispatch<SetStateAction<string | null>>;
  setScrollArrows: Dispatch<SetStateAction<{ showLeftArrow: boolean; showRightArrow: boolean; isHovered: boolean }>>;
  onCampaignSelectRef: RefObject<((c: CampaignSource) => void) | undefined>;
}

/**
 * Listens for manual scroll (swipe) gestures, updates the visual active card
 * immediately, and commits the selection to the store on scrollend.
 */
export function useScrollTracking({
  carouselRef,
  campaigns,
  isArrowsHovered,
  refs,
  getCachedElement,
  setSelectedCampaignId,
  setVisualActiveId,
  setScrollArrows,
  onCampaignSelectRef,
}: ScrollTrackingProps) {
  const { isProgrammaticScroll, targetCampaignId, isManualScrollingRef, visualActiveIdRef, selectionTimerRef } = refs;

  // Keep hover state in a ref so the scroll handler reads the latest value
  // without causing listener teardown/reattach on every hover toggle.
  const isArrowsHoveredRef = useRef(isArrowsHovered);
  isArrowsHoveredRef.current = isArrowsHovered;

  useEffect(() => {
    const container = carouselRef.current;
    if (!container) return;

    let rafId = 0;
    const hasScrollEnd = 'onscrollend' in container;

    const findCentered = (): CampaignSource | null => {
      const rect = container.getBoundingClientRect();
      const center = rect.left + container.offsetWidth / 2;
      let closest: CampaignSource | null = null;
      let closestDist = Infinity;
      for (const c of campaigns) {
        const el = getCachedElement(c.id);
        if (el) {
          const r = el.getBoundingClientRect();
          const dist = Math.abs(center - (r.left + r.width / 2));
          if (dist < closestDist) {
            closestDist = dist;
            closest = c;
          }
        }
      }
      return closest;
    };

    const handleScrollStart = () => {
      if (!isProgrammaticScroll.current && !isManualScrollingRef.current) {
        isManualScrollingRef.current = true;
      }
    };

    const commitSelection = () => {
      if (!isManualScrollingRef.current) return;

      const centered = findCentered();
      if (centered) {
        if (centered.id !== visualActiveIdRef.current) {
          setVisualActiveId(centered.id);
          visualActiveIdRef.current = centered.id;
        }
        setSelectedCampaignId(centered.id);
        onCampaignSelectRef.current?.(centered);
      }
      isManualScrollingRef.current = false;
    };

    const handleScroll = () => {
      if (rafId) return;

      rafId = requestAnimationFrame(() => {
        rafId = 0;
        if (!container) return;

        const scrollLeft = container.scrollLeft;
        const closestCampaign = findCentered();

        if (isArrowsHoveredRef.current) {
          const maxScrollLeft = container.scrollWidth - container.offsetWidth;
          const threshold = 10;
          setScrollArrows(prev => ({
            ...prev,
            showLeftArrow: scrollLeft > threshold,
            showRightArrow: scrollLeft < maxScrollLeft - threshold,
          }));
        }

        if (isProgrammaticScroll.current) {
          if (closestCampaign?.id === targetCampaignId.current) {
            isProgrammaticScroll.current = false;
            targetCampaignId.current = null;
          }
        } else {
          if (!isManualScrollingRef.current) {
            isManualScrollingRef.current = true;
          }
          if (closestCampaign && closestCampaign.id !== visualActiveIdRef.current) {
            setVisualActiveId(closestCampaign.id);
            visualActiveIdRef.current = closestCampaign.id;
          }

          if (!hasScrollEnd) {
            clearTimeout(selectionTimerRef.current);
            selectionTimerRef.current = setTimeout(commitSelection, 300);
          }
        }
      });
    };

    container.addEventListener('touchstart', handleScrollStart, { passive: true });
    container.addEventListener('scroll', handleScroll, { passive: true });
    if (hasScrollEnd) {
      container.addEventListener('scrollend', commitSelection, { passive: true });
    }

    return () => {
      container.removeEventListener('touchstart', handleScrollStart);
      container.removeEventListener('scroll', handleScroll);
      if (hasScrollEnd) {
        container.removeEventListener('scrollend', commitSelection);
      }
      if (rafId) cancelAnimationFrame(rafId);
      clearTimeout(selectionTimerRef.current);
    };
  // Refs are stable — only re-run when campaigns or getCachedElement change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaigns, getCachedElement, setSelectedCampaignId]);
}
